import React, { useContext, useMemo } from 'react';
import { unknownMembershipNumber, useMembership } from '../../hooks/useMembership';
import { Permissions } from '../Context/memberContext/types/Permissions';
import { useMembershipImpersonationContext } from '../MembershipImpersonation/MembershipImpersonationContext';
import { useMemberStaleAwareData } from '../Context/useMemberStaleAwareData';

type MemberPermissionsContextType = {
  permissions?: Permissions;
} & Partial<Permissions>;

const noAccessPermissions: Permissions = {
    workOps: 'NoAccess',
    sendCompanyDocuments: 'NoAccess',
    riskAssessments: 'NoAccess',
    companyDocuments: 'NoAccess',
    tradingUnits: 'NoAccess',
    training: 'NoAccess',
    rewards: 'NoAccess',
    wallet: 'NoAccess'
};

const MemberPermissionsContext = React.createContext<MemberPermissionsContextType>({
    permissions: undefined
});

export const MemberPermissionsProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const { membershipNumber } = useMembership();
    const { data: memberData } = useMemberStaleAwareData();
    const {
        accountManagerPermissions,
        impersonateMemberPermissions,
        isImpersonatingMember
    } = useMembershipImpersonationContext();

    const permissions = useMemo<Permissions | undefined>(() => {
        if (!memberData && membershipNumber !== unknownMembershipNumber) {
            return undefined;
        }

        if (isImpersonatingMember && !impersonateMemberPermissions) {
            return accountManagerPermissions ?? noAccessPermissions;
        }

        if (membershipNumber === unknownMembershipNumber) {
            return noAccessPermissions;
        }

        return memberData?.permissions ?? noAccessPermissions;

    }, [
        membershipNumber,
        memberData,
        isImpersonatingMember,
        impersonateMemberPermissions,
        accountManagerPermissions
    ]);

    return (
        <MemberPermissionsContext.Provider
            value={{
                permissions,
                ...(permissions ?? {})
            }}
        >
            {children}
        </MemberPermissionsContext.Provider>
    );
};

export const useMemberPermissions = () => useContext(MemberPermissionsContext);